import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { switchMap, filter, take, timeout, map } from 'rxjs/operators';

export interface TitleSuggestion {
  suggestions: string[];
  tips: string[];
}

@Injectable({ providedIn: 'root' })
export class TitleSuggestionService {
  private base = 'http://localhost:5000';

  constructor(private http: HttpClient) {}

  getSuggestions(title: string, category: string): Observable<TitleSuggestion> {
    const goal =
      `You are a copywriter for a home services marketplace called AlreadyDone.us. ` +
      `A contractor listed a service titled "${title}" in the "${category}" category. ` +
      `Suggest exactly 4 alternative titles that are more specific, benefit-focused, and likely to attract customer clicks. ` +
      `Each title should be under 60 characters. ` +
      `Format your response as a numbered list (1. 2. 3. 4.) for the titles, ` +
      `then write "Tips:" followed by exactly 2 bullet points (using - ) on how to make the listing more compelling.`;

    return this.http.post<any>(`${this.base}/api/run`, { goal }).pipe(
      switchMap(() => this.pollUntilDone()),
      switchMap(() => this.http.get<any>(`${this.base}/api/results`)),
      map(results => this.parseResults(results))
    );
  }

  private pollUntilDone(): Observable<any> {
    // Check every 3 seconds; give up after 90 seconds
    return timer(3000, 3000).pipe(
      switchMap(() => this.http.get<any>(`${this.base}/api/status`)),
      filter((status: any) => !status.running),
      take(1),
      timeout(90000)
    );
  }

  private parseResults(results: any): TitleSuggestion {
    const doc: string =
      results['pipeline.writing.result']?.result?.document || '';

    // Fallback: google ads headlines from the marketing agent
    const adHeadlines: string[] =
      results['pipeline.marketing.result']?.result?.copy?.google_ads?.headlines || [];

    const suggestions: string[] = [];
    const tips: string[] = [];
    let inTips = false;

    for (const raw of doc.split('\n')) {
      const line = raw.trim();
      if (!line) continue;

      if (/^tips?:/i.test(line)) { inTips = true; continue; }

      if (!inTips) {
        const m = line.match(/^\s*\d+[\.\)]\s+(.+)/);
        if (m && m[1].length <= 80 && suggestions.length < 4) {
          suggestions.push(m[1].replace(/\*+/g, '').trim());
        }
      } else {
        const m = line.match(/^[-*•]\s+(.+)/);
        if (m && tips.length < 2) {
          tips.push(m[1].replace(/\*+/g, '').trim());
        }
      }
    }

    // Use ad headlines as fallback suggestions
    if (suggestions.length === 0) {
      suggestions.push(...adHeadlines.slice(0, 4));
    }

    return { suggestions, tips };
  }
}
